import { Component, computed, inject, input, OnDestroy, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { ProductoImagenService } from '@shared/services/producto-imagen.service';

@Component({
  selector: 'app-producto-avatar',
  standalone: true,
  imports: [],
  templateUrl: './producto-avatar.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './producto-avatar.css',
})
export class ProductoAvatar implements OnDestroy {
  private productoImagenService = inject(ProductoImagenService);

  // Inputs
  editable = input<boolean>(true);

  initialUrl = input<string | null>(null);
  maxSizeMb = input<number>(this.productoImagenService.defaultMaxSizeMb);

  // Outputs
  imagenChange = output<File | null>();
  validationError = output<string | null>();

  // Internal state
  imagenArchivo = signal<File | null>(null);
  imagenPreviewUrl = signal<string | null>(null);
  tieneImagen = computed(() => !!this.imagenPreviewUrl() || !!this.initialUrl());
  private imagenPreviewObjectUrl: string | null = null;

  ngOnDestroy(): void {
    this.revokeImagenPreviewUrl();
  }

  onFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;

    if (!file) {
      this.imagenArchivo.set(null);
      this.imagenPreviewUrl.set(null);
      this.imagenChange.emit(null);
      this.validationError.emit(null);
      this.revokeImagenPreviewUrl();
      return;
    }

    const error = this.productoImagenService.validarImagen(file, this.maxSizeMb());
    if (error) {
      this.imagenArchivo.set(null);
      this.imagenPreviewUrl.set(null);
      input.value = '';
      this.imagenChange.emit(null);
      this.validationError.emit(error);
      return;
    }

    this.imagenArchivo.set(file);
    this.validationError.emit(null);
    this.revokeImagenPreviewUrl();
    this.imagenPreviewObjectUrl = URL.createObjectURL(file);
    this.imagenPreviewUrl.set(this.imagenPreviewObjectUrl);
    this.imagenChange.emit(file);
  }

  private revokeImagenPreviewUrl() {
    if (this.imagenPreviewObjectUrl) {
      URL.revokeObjectURL(this.imagenPreviewObjectUrl);
      this.imagenPreviewObjectUrl = null;
    }
  }

  // Exposed helper to clear selection from parent if needed
  clear() {
    this.imagenArchivo.set(null);
    this.imagenPreviewUrl.set(null);
    this.imagenChange.emit(null);
    this.validationError.emit(null);
    this.revokeImagenPreviewUrl();
  }
}
